/**
 * 구역 선택 컴포넌트
 * 사용자 친화적인 구역 선택 인터페이스
 */
import React, { useState } from 'react';
import {
  BuildingOffice2Icon,
  UserGroupIcon,
  SparklesIcon,
  BookOpenIcon,
  CakeIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useZones } from '../hooks/useZones';
import { Zone, ZONE_TYPE_LABELS } from '../services/zoneApi';

interface ZoneSelectionProps {
  onZoneSelect: (zone: Zone) => void;
  onBack?: () => void;
}

/**
 * 구역 타입별 아이콘 매핑
 */
const getZoneIcon = (zoneType: string) => {
  switch (zoneType) {
    case 'general':
      return BuildingOffice2Icon;
    case 'quiet':
      return ShieldCheckIcon;
    case 'premium':
      return SparklesIcon;
    case 'study_room':
      return BookOpenIcon;
    case 'cafe':
      return CakeIcon;
    case 'group':
      return UserGroupIcon;
    default:
      return BuildingOffice2Icon;
  }
};

/**
 * 구역 타입별 배경 그라데이션
 */
const getZoneGradient = (zoneType: string) => {
  switch (zoneType) {
    case 'general':
      return 'from-blue-400 to-blue-600';
    case 'quiet':
      return 'from-green-400 to-green-600';
    case 'premium':
      return 'from-purple-400 to-purple-600';
    case 'study_room':
      return 'from-orange-400 to-orange-600';
    case 'cafe':
      return 'from-amber-400 to-amber-600';
    case 'group':
      return 'from-pink-400 to-pink-600';
    default:
      return 'from-gray-400 to-gray-600';
  }
};

/**
 * 점유율에 따른 상태 표시
 */
const getOccupancyStatus = (occupancyRate: number) => {
  if (occupancyRate >= 90)
    return { label: '만석 임박', color: 'text-red-600', bgColor: 'bg-red-100' };
  if (occupancyRate >= 70)
    return {
      label: '혼잡',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    };
  if (occupancyRate >= 40)
    return {
      label: '보통',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    };
  return { label: '여유', color: 'text-green-600', bgColor: 'bg-green-100' };
};

const ZoneSelection: React.FC<ZoneSelectionProps> = ({
  onZoneSelect,
  onBack,
}) => {
  const { zones, isLoading, error, refreshZones } = useZones();
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 구역 타입별 필터링
  const filteredZones = zones.filter((zone) => {
    if (selectedFilter === 'all') return true;
    return zone.zone_type === selectedFilter;
  });

  // 사용 가능한 구역 타입들
  const availableTypes = Array.from(
    new Set(zones.map((zone) => zone.zone_type))
  );

  // 새로고침 핸들러
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshZones();
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">구역 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900">
            오류가 발생했습니다
          </h3>
          <p className="text-gray-600 mt-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* 헤더 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                뒤로가기
              </button>
            )}
            <h1 className="text-xl font-bold text-gray-900">구역 선택</h1>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                isRefreshing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
              title="구역 정보 새로고침"
            >
              <ArrowPathIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">새로고침</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 설명 섹션 */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            원하는 구역을 선택해주세요
          </h2>
          <p className="text-lg text-gray-600">
            각 구역별로 다른 분위기와 시설을 제공합니다
          </p>
        </div>

        {/* 필터 탭 */}
        <div className="flex justify-center mb-8">
          <div className="flex bg-white rounded-lg shadow-sm border p-1">
            <button
              onClick={() => setSelectedFilter('all')}
              className={`px-4 py-2 rounded-md font-medium transition-all ${
                selectedFilter === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              전체
            </button>
            {availableTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedFilter(type)}
                className={`px-4 py-2 rounded-md font-medium transition-all ${
                  selectedFilter === type
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                {ZONE_TYPE_LABELS[type] || type}
              </button>
            ))}
          </div>
        </div>

        {/* 구역 카드 그리드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredZones.map((zone) => {
            const IconComponent = getZoneIcon(zone.zone_type);
            const gradient = getZoneGradient(zone.zone_type);
            const occupancyStatus = getOccupancyStatus(zone.occupancy_rate);

            return (
              <div
                key={zone.id}
                onClick={() => onZoneSelect(zone)}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden group"
              >
                {/* 상단 그라데이션 헤더 */}
                <div
                  className={`bg-gradient-to-r ${gradient} px-6 py-4 text-white`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <IconComponent className="w-8 h-8 mr-3" />
                      <div>
                        <h3 className="text-xl font-bold">{zone.name}</h3>
                        <p className="text-white/90 text-sm">
                          {ZONE_TYPE_LABELS[zone.zone_type]}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">{zone.code}</div>
                    </div>
                  </div>
                </div>

                {/* 카드 내용 */}
                <div className="p-6">
                  {/* 좌석 현황 */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">좌석 현황</span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${occupancyStatus.bgColor} ${occupancyStatus.color} font-medium`}
                      >
                        {occupancyStatus.label}
                      </span>
                    </div>

                    {/* 진행률 바 */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          zone.occupancy_rate >= 90
                            ? 'bg-red-500'
                            : zone.occupancy_rate >= 70
                              ? 'bg-orange-500'
                              : zone.occupancy_rate >= 40
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                        }`}
                        style={{ width: `${zone.occupancy_rate}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between text-sm text-gray-600">
                      <span>사용 가능: {zone.available_seats}석</span>
                      <span>전체: {zone.total_seats}석</span>
                    </div>
                  </div>

                  {/* 요금 정보 */}
                  <div className="mb-4">
                    <div className="text-sm text-gray-600 mb-2">이용 요금</div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">시간당</span>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              ₩{(zone.base_hourly_rate + (zone.hourly_surcharge || 0)).toLocaleString()}
                            </div>
                            {zone.hourly_surcharge && zone.hourly_surcharge !== 0 && (
                              <div className="text-xs text-gray-500">
                                (기본 ₩{zone.base_hourly_rate.toLocaleString()} + 추가 ₩{zone.hourly_surcharge.toLocaleString()})
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">일일권</span>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              ₩{(zone.base_daily_rate + (zone.daily_surcharge || 0)).toLocaleString()}
                            </div>
                            {zone.daily_surcharge && zone.daily_surcharge !== 0 && (
                              <div className="text-xs text-gray-500">
                                (기본 ₩{zone.base_daily_rate.toLocaleString()} + 추가 ₩{zone.daily_surcharge.toLocaleString()})
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {(zone.hourly_surcharge || zone.daily_surcharge) && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-blue-600 font-medium">
                            🏷️ 이 구역은 특별 가격이 적용됩니다
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 구역 설명 */}
                  {zone.description && (
                    <p className="text-sm text-gray-600 mb-4">
                      {zone.description}
                    </p>
                  )}

                  {/* 선택 버튼 */}
                  <button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold group-hover:from-blue-600 group-hover:to-blue-700 transition-all duration-300 transform group-hover:scale-105">
                    {zone.available_seats > 0 ? '좌석 선택하기' : '대기열 등록'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* 구역이 없을 때 */}
        {filteredZones.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🏢</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {selectedFilter === 'all'
                ? '등록된 구역이 없습니다'
                : '해당하는 구역이 없습니다'}
            </h3>
            <p className="text-gray-600">
              {selectedFilter === 'all'
                ? '관리자에게 문의해주세요'
                : '다른 구역 타입을 선택해보세요'}
            </p>
            {selectedFilter !== 'all' && (
              <button
                onClick={() => setSelectedFilter('all')}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                전체 구역 보기
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneSelection;
